import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Settings, AlertCircle, CheckCircle, Clock, Download, Calendar } from 'lucide-react';
import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { Badge } from '@/components/Badge';
import { Loading } from '@/components/Loading';
import { Modal } from '@/components/Modal';
import { SLAPolicyForm } from '@/components/sla/SLAPolicyForm';
import { slaService } from '@/services/slaService';
import { exportService } from '@/services/exportService';
import type { SLAPolicy, CreateSLAPolicyRequest, UpdateSLAPolicyRequest } from '@/types/sla';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';

/**
 * SLA Policies Management Page
 * Provides comprehensive CRUD operations for SLA policies
 */
export const SLAPolicies: React.FC = () => {
  useDocumentTitle('SLA Policies | SupportDesk');
  const [policies, setPolicies] = useState<SLAPolicy[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedPolicy, setSelectedPolicy] = useState<SLAPolicy | null>(null);
  const [includeInactive, setIncludeInactive] = useState(false);

  useEffect(() => {
    loadPolicies();
  }, [includeInactive]);

  const loadPolicies = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await slaService.getAllPolicies();
      setPolicies(data);
    } catch (err) {
      setError('Failed to load SLA policies');
      console.error('Error loading SLA policies:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (data: CreateSLAPolicyRequest) => {
    try {
      console.log('🔄 Creating SLA Policy:', data);
      const result = await slaService.createPolicy(data);
      console.log('✅ SLA Policy Created Successfully:', result);
      setShowCreateModal(false);
      await loadPolicies();
      return result;
    } catch (err: any) {
      console.error('❌ Error creating SLA policy:', err);
      setError(`Failed to create SLA policy: ${err.message || 'Unknown error'}`);
      throw err;
    }
  };

  const handleEdit = async (data: UpdateSLAPolicyRequest) => {
    if (!selectedPolicy) {
      console.error('❌ No selected policy for edit operation');
      return;
    }
    
    try {
      console.log('🔄 Updating SLA Policy:', { id: selectedPolicy.id, data });
      const result = await slaService.updatePolicy(selectedPolicy.id, data);
      console.log('✅ SLA Policy Updated Successfully:', result);
      setShowEditModal(false);
      setSelectedPolicy(null);
      setError(null);
      await loadPolicies();
      return result;
    } catch (err: any) {
      console.error('❌ Error updating SLA policy:', err);
      setError(`Failed to update SLA policy: ${err.message || 'Unknown error'}`);
      throw err;
    }
  };

  const handleDelete = async () => {
    if (!selectedPolicy) {
      console.error('❌ No selected policy for delete operation');
      return;
    }
    
    try {
      console.log('🗑️ Deleting SLA Policy:', { id: selectedPolicy.id, name: selectedPolicy.name });
      await slaService.deletePolicy(selectedPolicy.id);
      console.log('✅ SLA Policy Deleted Successfully');
      setShowDeleteModal(false);
      setSelectedPolicy(null);
      setError(null);
      await loadPolicies();
    } catch (err: any) {
      console.error('❌ Error deleting SLA policy:', err);
      const errorMessage = err.message?.includes('in use') || err.message?.includes('referenced') 
        ? 'Cannot delete SLA policy: It is currently being used by tickets or other resources.'
        : `Failed to delete SLA policy: ${err.message || 'Unknown error'}`;
      setError(errorMessage);
    }
  };

  const handleExport = async () => {
    try {
      setError(null);
      await exportService.exportSLAPolicies();
    } catch (err) {
      console.error('Error exporting SLA policies:', err);
      setError('Failed to export SLA policies');
    }
  };

  const formatTimeTarget = (minutes: number): string => {
    if (minutes < 60) return `${minutes}m`;
    if (minutes < 1440) return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
    const days = Math.floor(minutes / 1440);
    const hours = Math.floor((minutes % 1440) / 60);
    return `${days}d ${hours}h`;
  };

  const getTierColor = (tier: string): string => {
    const colors = {
      'Basic': 'bg-gray-100 text-gray-800',
      'Premium': 'bg-blue-100 text-blue-800',
      'Enterprise': 'bg-purple-100 text-purple-800',
      'VIP': 'bg-gold-100 text-gold-800',
      'Custom': 'bg-green-100 text-green-800'
    };
    return colors[tier as keyof typeof colors] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <Loading size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <Card className="p-6 text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Error Loading SLA Policies</h3>
          <p className="text-gray-600 mb-4">{error}</p>
          <Button onClick={loadPolicies} variant="primary">
            Try Again
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <AlertCircle className="h-5 w-5 text-red-500 mr-2" />
            <span className="text-red-700">{error}</span>
            <button 
              onClick={() => setError(null)} 
              className="ml-auto text-red-500 hover:text-red-700"
              aria-label="Dismiss error"
            >
              ×
            </button>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">SLA Policies</h1>
          <p className="text-gray-600 mt-1">
            Configure service level agreements and response time targets
          </p>
        </div>
        <div className="flex space-x-3">
          <Button
            variant="outline"
            icon={<Download className="h-4 w-4" />}
            onClick={handleExport}
            disabled={loading}
          >
            Export CSV
          </Button>
          <Button
            variant="primary"
            icon={<Plus className="h-4 w-4" />}
            onClick={() => setShowCreateModal(true)}
            data-testid="create-sla-policy"
            aria-label="Create new SLA policy"
          >
            Create Policy
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={includeInactive}
                onChange={(e) => setIncludeInactive(e.target.checked)}
                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
              />
              <span className="ml-2 text-sm text-gray-700">Include inactive policies</span>
            </label>
          </div>
          <div className="text-sm text-gray-500">
            {policies.length} {policies.length === 1 ? 'policy' : 'policies'}
          </div>
        </div>
      </Card>

      {/* Policies Grid */}
      {policies.length === 0 ? (
        <Card className="p-12 text-center">
          <Settings className="h-16 w-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No SLA Policies</h3>
          <p className="text-gray-600 mb-6">
            Get started by creating your first SLA policy to define response time targets.
          </p>
          <Button
            variant="primary"
            icon={<Plus className="h-4 w-4" />}
            onClick={() => setShowCreateModal(true)}
            data-testid="create-first-sla-policy"
            aria-label="Create first SLA policy"
          >
            Create First Policy
          </Button>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
          {policies.map((policy) => (
            <Card key={policy.id} className="p-6">
              {/* Policy Header */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-2">
                    <h3 className="text-lg font-semibold text-gray-900">{policy.name}</h3>
                    {policy.isDefault && (
                      <Badge variant="success" size="sm">
                        Default
                      </Badge>
                    )}
                  </div>
                  <div className="flex items-center space-x-2">
                    <Badge className={getTierColor(policy.tier)}>
                      {policy.tier}
                    </Badge>
                    <Badge variant={policy.isActive ? 'success' : 'secondary'}>
                      {policy.isActive ? 'Active' : 'Inactive'}
                    </Badge>
                  </div>
                </div>
                <div className="flex items-center space-x-1 ml-4">
                  <Button
                    variant="ghost"
                    size="sm"
                    icon={<Edit className="h-4 w-4" />}
                    onClick={() => {
                      setSelectedPolicy(policy);
                      setShowEditModal(true);
                    }}
                    data-testid={`edit-sla-policy-${policy.id}`}
                    title="Edit SLA policy"
                    aria-label={`Edit SLA policy ${policy.name}`}
                  />
                  <Button
                    variant="ghost"
                    size="sm"
                    icon={<Trash2 className="h-4 w-4" />}
                    onClick={() => {
                      setSelectedPolicy(policy);
                      setShowDeleteModal(true);
                    }}
                    disabled={policy.isDefault}
                    data-testid={`delete-sla-policy-${policy.id}`}
                    title="Delete SLA policy"
                    aria-label={`Delete SLA policy ${policy.name}`}
                  />
                </div>
              </div>

              {/* Description */}
              {policy.description && (
                <p className="text-sm text-gray-600 mb-4">{policy.description}</p>
              )}

              {/* Response Time Targets */}
              <div className="space-y-3 mb-4">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">First Response</span>
                  <div className="flex items-center space-x-2">
                    <Clock className="h-3 w-3 text-gray-400" />
                    <span className="font-medium">
                      {formatTimeTarget(policy.firstResponseCriticalPriorityMinutes)} - {formatTimeTarget(policy.firstResponseLowPriorityMinutes)}
                    </span>
                  </div>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Resolution</span>
                  <div className="flex items-center space-x-2">
                    <CheckCircle className="h-3 w-3 text-gray-400" />
                    <span className="font-medium">
                      {formatTimeTarget(policy.resolutionCriticalPriorityMinutes)} - {formatTimeTarget(policy.resolutionLowPriorityMinutes)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Business Hours Info */}
              <div className="text-xs text-gray-500 border-t pt-3">
                <div className="flex items-center justify-between">
                  <span>Business Hours Only</span>
                  <span>{policy.businessHoursOnly ? 'Yes' : 'No'}</span>
                </div>
                <div className="flex items-center justify-between mt-1">
                  <span>Auto Pause</span>
                  <span>{policy.autoPauseOnCustomerWait ? 'Yes' : 'No'}</span>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Configuration Links */}
      <Card className="p-6 bg-blue-50 border-blue-200">
        <div className="flex items-start justify-between">
          <div className="flex items-start">
            <AlertCircle className="h-5 w-5 text-blue-500 mr-3 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-blue-700">
              <p className="font-medium mb-2">SLA Configuration</p>
              <ul className="list-disc list-inside space-y-1">
                <li>SLA policies define response time targets for different priority levels</li>
                <li>Business hours calendars control when SLA timers are active</li>
                <li>Holidays and weekends can be automatically excluded from SLA calculations</li>
                <li>Multiple policies support different service tiers and customer types</li>
              </ul>
            </div>
          </div>
          <Button
            variant="outline"
            icon={<Calendar className="h-4 w-4" />}
            onClick={() => window.location.href = '/sla/business-hours'}
            data-testid="manage-business-hours"
            className="ml-4 flex-shrink-0"
          >
            Manage Business Hours & Holidays
          </Button>
        </div>
      </Card>

      {/* Create Modal */}
      <Modal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        title="Create SLA Policy"
        size="lg"
        data-testid="create-sla-policy-modal"
      >
        <SLAPolicyForm
          onSubmit={handleCreate}
          onCancel={() => setShowCreateModal(false)}
        />
      </Modal>

      {/* Edit Modal */}
      <Modal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setSelectedPolicy(null);
        }}
        title="Edit SLA Policy"
        size="lg"
        data-testid="edit-sla-policy-modal"
      >
        {selectedPolicy && (
          <SLAPolicyForm
            initialData={selectedPolicy}
            onSubmit={handleEdit}
            onCancel={() => {
              setShowEditModal(false);
              setSelectedPolicy(null);
            }}
          />
        )}
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setSelectedPolicy(null);
        }}
        title="Delete SLA Policy"
      >
        <div className="space-y-4">
          <p className="text-gray-600">
            Are you sure you want to delete the policy "{selectedPolicy?.name}"? 
            This action cannot be undone.
          </p>
          <div className="flex items-center space-x-3 pt-4">
            <Button
              variant="danger"
              onClick={handleDelete}
              loading={loading}
            >
              Delete Policy
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                setShowDeleteModal(false);
                setSelectedPolicy(null);
              }}
            >
              Cancel
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};
